import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  LinearProgress,
  Divider,
  Dialog,
  Slide,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUser,
  userGetStartedHasFinishedSelector,
  userPlanSelector,
  userSetupQuestionsSelector,
} from "src/store/system/selector";
import { USER_PLAN as UserPlan } from "src/enums/common.enum";
import type { SetupCardCompletionSource } from "src/interfaces/event";
import ConnectBanksCard from "src/pages/GetStarted/Onboarding/FeatureCards/ConnectBanksCard";
import ConnectPlatformsCard from "src/pages/GetStarted/Onboarding/FeatureCards/ConnectPlatformsCard";
import ManualUploadCard from "src/pages/GetStarted/Onboarding/FeatureCards/ManualUploadCard";
import NormalPlatformCard from "src/pages/GetStarted/Onboarding/PlatformCards/NormalPlatformCard";
import ExtensionPlatformCard from "src/pages/GetStarted/Onboarding/PlatformCards/ExtensionPlatformCard";
import { useHistory } from "react-router-dom";
import { setOpen, setStepType } from "src/store/tour/actions";
import { TourState } from "src/interfaces/joyrideState.interface";
import { makeStyles } from "@material-ui/core/styles";
import MercariConfirm from "src/pages/GetStarted/MercariConfirm";
import PoshmarkConfirm from "src/pages/GetStarted/PoshmarkConfirm";
import DepopConfirm from "src/pages/GetStarted/DepopConfirm";
import VintedConfirm from "src/pages/GetStarted/VintedConfirm";
import { etsyRequestAuthorizationCode } from "src/apiService/modules/etsy";
import { useLoadingBackdrop } from "src/contexts/LoadingBackdropContext";
import { toast } from "react-toastify";
import { getAmazonOAuthUrl } from "src/apiService/modules/amazon";
import { getEBayOAuthUrl, updateUserApi } from "src/apiService";
import HideSetupPageButton from "src/components/Onboarding/HideSetupPageButton";
import {
  getAmazonIntegrations,
  getEBayIntegrations,
  getEtsyIntegrations,
} from "src/store/plaidIntegration/selector";
import useIsRunning from "src/pages/Integrations/useIsRunning";
import { IsRunningThreshold } from "src/store/integrationSync/actions/utils";
import LOGO from "src/assets/svgs/logo.svg";
import { CheckCircle } from "@material-ui/icons";
import type { Theme } from "src/theme";
import Container from "@material-ui/core/Container";
import Button from "@material-ui/core/Button";
import { InventoryTrackingModal } from "src/pages/GetStarted/Onboarding/InventoryTrackingModal";
import {
  completeUserInventoryTypeModal,
  completeUserSetupQuestionCardPlatform,
  doesUserHaveCardPlatform,
} from "src/apiService/modules/users";
import {
  isImplementedIntegration,
  type KeyedCard,
} from "src/pages/GetStarted/Onboarding/utils";
import PoshmarkConfirmDialog from "src/components/PoshmarkConfirmDialog";
import {
  getWhatnotIntegration,
  whatnotIntegrationCreate,
} from "src/apiService/modules/whatnot";
import { getWhatnotUser } from "src/utils/extension/whatnot";
import type { Cookie } from "src/utils/extension";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { whatnotSyncSales } from "src/store/integrationSync/actions/whatnot";
import type MonthYear from "src/interfaces/monthYear.interface";
import {
  cardPlatformConnectionsSelector,
  type CardPlatform,
} from "src/pages/GetStarted/Onboarding/selector";
import {
  useCanAddMoreEBayIntegrations,
  WantToAddAdditionalEBayStoresContent,
} from "src/pages/GetStarted/AddNewIntegrationDialog";

type WhatnotUserData =
  | { username: string; userId: string; cookies: Cookie[] }
  | { error: string }
  | { loading: true };

const CARD_FADE_S = 0.8;
/**
 * How long the success screen sits before an automatic finish fades it out.
 * Long enough for its fade-in to land and the message to be read — starting the
 * fade-out immediately makes the two animations overlap and look broken.
 */
const AUTO_FINISH_DWELL_MS = 2000;

const useStyles = makeStyles<Theme, { progressPercent: number }>((theme) => ({
  minGap: {
    gap: theme.spacing(2),
  },
  progressBar: {
    height: theme.spacing(5),
    borderRadius: theme.spacing(6),
  },
  progressLabel: {
    position: "absolute" as const,
    top: 0,
    bottom: 0,
    left: ({ progressPercent }) =>
      `max(${theme.spacing(6)}px, ${progressPercent}%)`,
    display: "flex",
    alignItems: "center",
    transform: "translateX(-100%)",
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    color:
      theme.palette.custom.mode === "dark"
        ? theme.palette.custom.derived.onAccent
        : theme.palette.custom.derived.onPrimary,
  },
  checkCircleIcon: {
    color: theme.palette.primary.main,
    fontSize: 48,
  },
  successMessage: {
    marginLeft: "1rem",
  },
  finishContainer: {
    transition: `opacity ${CARD_FADE_S}s ease-out, filter ${CARD_FADE_S}s ease-out`,
  },
  finishFadingOut: {
    opacity: 0,
    filter: "blur(8px)",
  },
  logoIcon: {
    width: "100%",
    filter:
      theme.palette.custom.mode === "dark" ? "brightness(0) invert(1)" : "none",
  },
}));

const SetupProgress: React.FC = () => {
  const recordEvent = useRecordEvent();
  const user = useSelector(selectUser);
  const setupQuestions = useSelector(userSetupQuestionsSelector);
  const history = useHistory();
  const [todoCards, setTodoCards] = useState<KeyedCard[]>([]);
  const [doneCards, setDoneCards] = useState<KeyedCard[]>([]);
  const [points, setPoints] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);
  const [isEbayUpgradeModalOpen, setIsEbayUpgradeModalOpen] = useState(false);
  const dispatch = useDispatch();
  const progressPercent =
    totalPoints > 0 ? Math.round((points / totalPoints) * 100) : 0;
  const classes = useStyles({ progressPercent });
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [finishing, setFinishing] = useState(false);
  const [askForInventory, setAskForInventory] = useState(false);
  const etsyIntegrations = useSelector(getEtsyIntegrations);
  const ebayIntegrations = useSelector(getEBayIntegrations);
  const amazonIntegrations = useSelector(getAmazonIntegrations);
  const [whatnotUserData, setWhatnotUserData] = useState<WhatnotUserData>({
    loading: true,
  });
  const mountedRef = useRef(false);
  const inventoryDismissedRef = useRef(false);
  const plan = useSelector(userPlanSelector) || UserPlan.BASIC;
  const isBasic = plan === UserPlan.BASIC;

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      mountedRef.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const hasNonIntegratedPlatforms = useMemo(
    () =>
      !!setupQuestions &&
      setupQuestions.sellingPlatforms.some(
        (platform) => !isImplementedIntegration(platform)
      ),
    [setupQuestions]
  );
  const isConnectCardPlatform = (platform: string) =>
    platform === "Ebay" ||
    platform === "Etsy" ||
    platform === "Mercari" ||
    platform === "Poshmark" ||
    platform === "Whatnot" ||
    platform === "Vinted" ||
    platform === "Depop";
  const selectedPlatforms = useMemo(
    () =>
      setupQuestions
        ? Array.from(
            new Set([
              ...setupQuestions.sellingPlatforms,
              ...(setupQuestions.finishedCards?.sellingPlatforms ?? []),
            ])
          )
        : [],
    [setupQuestions]
  );
  const manualUploadPlatforms = useMemo(
    () =>
      setupQuestions?.hasExistingRecords
        ? selectedPlatforms
        : selectedPlatforms.filter(
            (platform) => !isImplementedIntegration(platform)
          ),
    [selectedPlatforms, setupQuestions?.hasExistingRecords]
  );
  const declinedInventoryImport = !!setupQuestions?.declinedInventoryImport;
  // Declining the inventory import leaves the user with no inventory data and
  // no way in, so they get the card that teaches them to add it by hand. The
  // flag is only written alongside inventoryTypeSet, so by the time this turns
  // true the inventory prompt has already closed and can't be re-triggered.
  const needsManualUploadCard = useMemo(
    () =>
      !!setupQuestions &&
      (!!setupQuestions.hasExistingRecords ||
        hasNonIntegratedPlatforms ||
        !!setupQuestions.declinedInventoryImport),
    [hasNonIntegratedPlatforms, setupQuestions]
  );

  // Basic plans can't connect banks/credit cards — the Integrations page hides
  // the connect button for them — so never surface the card. Without this, an
  // account that answered yes and then downgraded could never finish setup.
  const needsConnectBankCard = !isBasic && !!setupQuestions?.connectBank;
  const areAllCardsComplete = setupQuestions
    ? !needsConnectBankCard &&
      setupQuestions.sellingPlatforms.filter(isConnectCardPlatform).length ===
        0 &&
      (!needsManualUploadCard || !!setupQuestions.finishedCards?.manualUpload)
    : true;
  const arePlatformCardsComplete = setupQuestions
    ? setupQuestions.sellingPlatforms.filter(isConnectCardPlatform).length ===
        0 &&
      (!setupQuestions.hasExistingRecords ||
        !!setupQuestions.finishedCards?.connectPlatforms) &&
      (!needsManualUploadCard || !!setupQuestions.finishedCards?.manualUpload)
    : false;
  const shouldPromptInventorySetup = setupQuestions
    ? setupQuestions.inventoryTypeSet === false && arePlatformCardsComplete
    : false;

  useEffect(() => {
    if (inventoryDismissedRef.current) return;
    setAskForInventory(shouldPromptInventorySetup);
  }, [shouldPromptInventorySetup]);

  const isFinished = setupQuestions
    ? areAllCardsComplete && setupQuestions.inventoryTypeSet !== false
    : true;
  const hasFinishedSetup = useSelector(userGetStartedHasFinishedSelector);
  const autoFinishedRef = useRef(false);

  const recordSetupCompletion = useCallback(
    (source: SetupCardCompletionSource) => {
      updateUserApi({
        getStartedEnd: new Date(),
        getStartedStep: "complete-set-up",
      });
      recordEvent({
        type: "feature-adoption",
        feature: "setup-completed",
        source,
      });
    },
    [recordEvent]
  );

  const fadeOutToTransactionDetails = useCallback(() => {
    setFinishing(true);
    setTimeout(() => {
      history.push("/transaction-details");
    }, CARD_FADE_S * 1000);
  }, [history]);

  // Completion used to hinge entirely on the "Finish Set Up" button, so anyone
  // who walked away from the success screen stayed unfinished forever — the Get
  // Started nav item only disappears once getStartedStep is "complete-set-up".
  // This heals that on the next load. Deliberately load-only: a user who
  // finishes their last card in this session still gets to press the button.
  useEffect(() => {
    if (autoFinishedRef.current) return;
    autoFinishedRef.current = true;
    if (!isFinished || hasFinishedSetup) return;
    // Persist before the dwell, so closing the tab mid-animation still leaves
    // them finished — that durability is the whole point of this fallback.
    recordSetupCompletion("auto-detected");
    const id = setTimeout(fadeOutToTransactionDetails, AUTO_FINISH_DWELL_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load-only: intentionally reads these as they were when the page opened
  }, []);

  const isEtsyRunning = useIsRunning(
    etsyIntegrations[0] ? etsyIntegrations[0].salesIsRunning : undefined,
    etsyIntegrations[0] ? etsyIntegrations[0].inventoryIsRunning : undefined,
    IsRunningThreshold
  );
  const isEbayRunning = useIsRunning(
    ebayIntegrations[0] ? ebayIntegrations[0].salesIsRunning : undefined,
    ebayIntegrations[0] ? ebayIntegrations[0].inventoryIsRunning : undefined,
    IsRunningThreshold
  );
  const isAmazonRunning = useIsRunning(
    amazonIntegrations[0] ? amazonIntegrations[0].salesIsRunning : undefined,
    amazonIntegrations[0]
      ? amazonIntegrations[0].inventoryIsRunning
      : undefined,
    IsRunningThreshold
  );
  const canAddMoreEBayIntegrations = useCanAddMoreEBayIntegrations();
  const backdrop = useLoadingBackdrop();
  const platformConnections = useSelector(cardPlatformConnectionsSelector);
  const autoCompletedRef = useRef<Set<string>>(new Set());

  // Credit platforms the user already connected somewhere other than these
  // cards — an account that predates setup, or the Integrations page. Card
  // completion is only written by the connect flows, so without this the card
  // sits unfinished forever. For eBay it is an outright dead end: a seller who
  // has already used up their store quota only ever gets the upgrade modal.
  // Platforms with room left keep their card so they can add another account.
  useEffect(() => {
    const pending = setupQuestions?.sellingPlatforms;
    if (!pending?.length) return;
    for (const platform of pending) {
      const connection = platformConnections[platform as CardPlatform];
      if (!connection?.connected || connection.canAddAnother) continue;
      if (autoCompletedRef.current.has(platform)) continue;
      autoCompletedRef.current.add(platform);
      completeUserSetupQuestionCardPlatform(platform, {
        source: "auto-detected",
      }).catch(() => {
        autoCompletedRef.current.delete(platform);
      });
    }
  }, [platformConnections, setupQuestions?.sellingPlatforms]);

  const connectIntegration = useCallback(
    async (platform: string) => {
      if (platform === "Etsy") {
        try {
          const authResponse = await etsyRequestAuthorizationCode({
            back: "/get-started#other:do-you-sell",
          });
          backdrop.current.show();
          window.location.href = authResponse.data.etsyURL;
        } catch (e) {
          toast.error(`There was an error: ${(e as Error).toString()}`);
        }
      }
      if (platform === "Amazon") {
        try {
          const {
            data: { url },
          } = await getAmazonOAuthUrl({
            back: "/get-started#amazon:import",
          });
          window.location = url;
        } catch (e) {
          toast.error(`There was an error: ${(e as Error).toString()}`);
        }
      }
      if (platform === "Ebay") {
        if (!canAddMoreEBayIntegrations) {
          setIsEbayUpgradeModalOpen(true);
          return;
        }
        try {
          const {
            data: { url },
          } = await getEBayOAuthUrl({
            integrationName: `ebay - ${user?.email}`,
            back: "/get-started#ebay:import",
          });
          window.location = url;
        } catch (e) {
          toast.error(`There was an error: ${(e as Error).toString()}`);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- backdrop is a stable ref
    [canAddMoreEBayIntegrations, user?.email]
  );

  useEffect(() => {
    const clickSetupCard = (cardType: TourState["stepType"]) => {
      dispatch(setStepType(cardType));
      dispatch(setOpen(true));
    };

    let totalPoints = 0;
    let donePoints = 0;
    if (setupQuestions) {
      const hasIntegrationPlatforms = setupQuestions.sellingPlatforms.some(
        isConnectCardPlatform
      );
      const skipDataGate =
        !hasIntegrationPlatforms || !!setupQuestions.hasExistingRecords;

      let combinedCards: KeyedCard[] = [];

      if (setupQuestions.hasExistingRecords) {
        if (!setupQuestions.finishedCards?.connectPlatforms) {
          combinedCards.push({
            key: "connect-platforms",
            element: (
              <ConnectPlatformsCard
                onClick={() => clickSetupCard("connect-platforms")}
              />
            ),
          });
        }
        totalPoints = totalPoints + 30;
      } else {
        const sellingPlatformCards: KeyedCard[] = [];
        for (let i = 0; i < setupQuestions.sellingPlatforms.length; i++) {
          const platform = setupQuestions.sellingPlatforms[i];
          if (isConnectCardPlatform(platform)) {
            if (
              platform === "Mercari" ||
              platform === "Poshmark" ||
              platform === "Whatnot" ||
              platform === "Vinted" ||
              platform === "Depop"
            ) {
              sellingPlatformCards.push({
                key: platform,
                element: (
                  <ExtensionPlatformCard
                    platform={platform}
                    onClick={async () => {
                      setSelectedPlatform(platform);
                      setIsPlatformModalOpen(true);
                      if (platform === "Whatnot") {
                        const whatnotData = await getWhatnotUser();
                        setWhatnotUserData(whatnotData);
                      }
                    }}
                  />
                ),
              });
            } else if (platform === "Ebay" || platform === "Etsy") {
              // To enable Amazon onboarding (still behind the "amazon"
              // feature flag): add "Amazon" to isConnectCardPlatform and to
              // this condition, then include isAmazonRunning below, e.g.
              //   const isPlatformRunning =
              //     platform === "Etsy"
              //       ? isEtsyRunning
              //       : platform === "Amazon"
              //       ? isAmazonRunning
              //       : isEbayRunning;
              // connectIntegration already handles the Amazon OAuth redirect.
              const isPlatformRunning =
                platform === "Etsy" ? isEtsyRunning : isEbayRunning;
              // Only surfaces when the platform still has room for another
              // account; anything at its cap has already been auto-completed.
              const connection = platformConnections[platform];
              sellingPlatformCards.push({
                key: platform,
                element: (
                  <NormalPlatformCard
                    platform={platform}
                    onClick={async () => {
                      setSelectedPlatform(platform);
                      await connectIntegration(platform);
                    }}
                    isRunning={isPlatformRunning}
                    connectedCount={
                      connection.canAddAnother ? connection.count : 0
                    }
                  />
                ),
              });
            }
            totalPoints = totalPoints + 30;
          }
        }
        combinedCards.push(...sellingPlatformCards);
      }
      if (needsManualUploadCard) {
        if (!setupQuestions.finishedCards?.manualUpload) {
          combinedCards.push({
            key: "manual-upload",
            element: (
              <ManualUploadCard
                onClick={() => clickSetupCard("manual-upload")}
                platforms={manualUploadPlatforms}
                // A user who declined the import has no data yet — that is the
                // whole reason for this card — so never gate it behind sales.
                skipDataGate={skipDataGate || declinedInventoryImport}
              />
            ),
          });
        }
        totalPoints = totalPoints + 15;
      }
      if (needsConnectBankCard) {
        combinedCards.push({
          key: "connect-bank",
          element: (
            <ConnectBanksCard
              onClick={() => clickSetupCard("connect-bank")}
              skipDataGate={skipDataGate}
            />
          ),
        });
        totalPoints = totalPoints + 15;
      }
      setTodoCards(combinedCards);

      if (setupQuestions.finishedCards) {
        const finishedCombinedCards: KeyedCard[] = [];
        const finishedPlatformCards: KeyedCard[] = [];

        if (setupQuestions.hasExistingRecords) {
          if (setupQuestions.finishedCards.connectPlatforms) {
            finishedCombinedCards.push({
              key: "connect-platforms",
              element: (
                <ConnectPlatformsCard
                  onClick={() => clickSetupCard("connect-platforms")}
                  completed={true}
                />
              ),
            });
            donePoints = donePoints + 30;
          }
        } else if (setupQuestions.finishedCards.sellingPlatforms) {
          for (
            let i = 0;
            i < setupQuestions.finishedCards.sellingPlatforms.length;
            i++
          ) {
            const platform = setupQuestions.finishedCards.sellingPlatforms[i];
            if (isConnectCardPlatform(platform)) {
              if (
                platform === "Mercari" ||
                platform === "Poshmark" ||
                platform === "Whatnot" ||
                platform === "Vinted" ||
                platform === "Depop"
              ) {
                finishedPlatformCards.push({
                  key: platform,
                  element: (
                    <ExtensionPlatformCard
                      platform={platform}
                      onClick={() => {
                        setSelectedPlatform(platform);
                        setIsPlatformModalOpen(true);
                      }}
                      completed={true}
                    />
                  ),
                });
              } else if (platform === "Ebay" || platform === "Etsy") {
                // To enable Amazon onboarding (still behind the "amazon"
                // feature flag): add "Amazon" to isConnectCardPlatform and to
                // this condition. connectIntegration already handles the
                // Amazon OAuth redirect.
                finishedPlatformCards.push({
                  key: platform,
                  element: (
                    <NormalPlatformCard
                      platform={platform}
                      onClick={async () => {
                        setSelectedPlatform(platform);
                        await connectIntegration(platform);
                      }}
                      completed={true}
                    />
                  ),
                });
              }
              totalPoints = totalPoints + 30;
              donePoints = donePoints + 30;
            }
          }
        }
        if (
          needsManualUploadCard &&
          setupQuestions.finishedCards.manualUpload
        ) {
          finishedCombinedCards.push({
            key: "manual-upload",
            element: (
              <ManualUploadCard
                onClick={() => clickSetupCard("manual-upload")}
                completed={true}
                platforms={manualUploadPlatforms}
              />
            ),
          });
          donePoints = donePoints + 15;
        }
        if (setupQuestions.finishedCards.connectBank) {
          finishedCombinedCards.push({
            key: "connect-bank",
            element: (
              <ConnectBanksCard
                onClick={() => {
                  clickSetupCard("connect-bank");
                }}
                completed={true}
              />
            ),
          });
          totalPoints = totalPoints + 15;
          donePoints = donePoints + 15;
        }
        finishedCombinedCards.push(...finishedPlatformCards);
        setDoneCards(finishedCombinedCards);
      }
    }
    setTotalPoints(totalPoints);
    setPoints(donePoints);
  }, [
    setupQuestions,
    declinedInventoryImport,
    manualUploadPlatforms,
    needsManualUploadCard,
    needsConnectBankCard,
    connectIntegration,
    isAmazonRunning,
    isEbayRunning,
    isEtsyRunning,
    platformConnections,
    setTodoCards,
    setDoneCards,
    dispatch,
    history,
  ]);

  return (
    <>
      <InventoryTrackingModal
        open={askForInventory}
        onClose={(declinedInventoryImport) => {
          inventoryDismissedRef.current = true;
          completeUserInventoryTypeModal({ declinedInventoryImport });
          setAskForInventory(false);
        }}
      />
      <AnimatePresence exitBeforeEnter initial={false}>
        {isFinished && !askForInventory ? (
          <motion.div
            key="setup-finished"
            initial={{ opacity: 0, filter: "blur(8px)" }}
            animate={{
              opacity: 1,
              filter: "blur(0px)",
              transition: { duration: CARD_FADE_S },
            }}
          >
            <Container
              maxWidth="sm"
              className={`${classes.finishContainer} ${
                finishing ? classes.finishFadingOut : ""
              }`}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mb={6}
              >
                <img
                  src={LOGO}
                  alt="My Reseller Genie"
                  className={classes.logoIcon}
                />
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                mb={6}
              >
                <Slide
                  direction="up"
                  in={true}
                  mountOnEnter
                  unmountOnExit
                  timeout={600}
                >
                  <CheckCircle className={classes.checkCircleIcon} />
                </Slide>
              </Box>
              <Box width="100%" justifyContent="center">
                <Box mb={3}>
                  <Typography
                    variant="body1"
                    className={classes.successMessage}
                    align="center"
                  >
                    Congratulations! You’ve completed the set-up process in My
                    Reseller Genie. Now you can start enjoying the benefits of
                    being on top of your numbers!
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={finishing}
                  onClick={() => {
                    recordSetupCompletion("flow");
                    fadeOutToTransactionDetails();
                  }}
                  color="primary"
                  autoFocus
                >
                  Finish Set Up
                </Button>
              </Box>
            </Container>
          </motion.div>
        ) : (
          <motion.div
            key="setup-in-progress"
            style={{ width: "100%" }}
            initial={{ opacity: 1, filter: "blur(0px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            exit={{
              opacity: 0,
              filter: "blur(8px)",
              transition: { duration: CARD_FADE_S },
            }}
          >
            <Box width="100%">
              <Typography variant="h6" gutterBottom align="center">
                Set Up Progress
              </Typography>
              <Box position="relative">
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  className={classes.progressBar}
                />
                <Box className={classes.progressLabel}>
                  <Typography variant="body2">{progressPercent}%</Typography>
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                mt={3}
                flexDirection="column"
              >
                <Box mb={5}>
                  <Typography variant="h5" gutterBottom>
                    To Do:
                  </Typography>
                  {todoCards.length > 0 && (
                    <Box
                      display="flex"
                      justifyContent={"center"}
                      flexWrap={"wrap"}
                      className={classes.minGap}
                    >
                      <AnimatePresence>
                        {todoCards.map(({ key, element }) => (
                          <motion.div
                            key={key}
                            layout
                            initial={
                              mountedRef.current
                                ? { opacity: 0, y: 20, scale: 0.95 }
                                : false
                            }
                            animate={{
                              opacity: 1,
                              y: 0,
                              scale: 1,
                              filter: "blur(0px)",
                              transition: {
                                type: "spring",
                                stiffness: 200,
                                damping: 20,
                              },
                            }}
                            exit={{
                              opacity: 0,
                              filter: "blur(6px)",
                              transition: { duration: CARD_FADE_S },
                            }}
                          >
                            {element}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </Box>
                  )}
                  {todoCards.length === 0 && (
                    <Box
                      display="flex"
                      justifyContent={"center"}
                      flexWrap={"wrap"}
                      className={classes.minGap}
                    >
                      <Typography variant="h5" gutterBottom>
                        Congratulations! You've completed the onboarding
                        process!
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Divider />
                <Box flex={1} mt={5}>
                  <Typography variant="h5" gutterBottom>
                    Done:
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent={"center"}
                    flexWrap={"wrap"}
                    className={classes.minGap}
                  >
                    <AnimatePresence>
                      {doneCards.map(({ key, element }) => (
                        <motion.div
                          key={key}
                          layout
                          initial={
                            mountedRef.current
                              ? { opacity: 0, filter: "blur(6px)" }
                              : false
                          }
                          animate={{
                            opacity: 1,
                            filter: "blur(0px)",
                            transition: {
                              duration: CARD_FADE_S,
                              delay: CARD_FADE_S,
                            },
                          }}
                        >
                          {element}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="center" mt="20px" mb="0px">
                  <HideSetupPageButton />
                </Box>
              </Box>
              <Dialog
                open={isPlatformModalOpen}
                onClose={() => setIsPlatformModalOpen(false)}
              >
                {selectedPlatform === "Mercari" && (
                  <MercariConfirm
                    onClose={() => setIsPlatformModalOpen(false)}
                    onStepChange={() => setIsPlatformModalOpen(false)}
                    showConnectSalePlatform={false}
                  />
                )}
                {selectedPlatform === "Vinted" && (
                  <VintedConfirm
                    onClose={() => setIsPlatformModalOpen(false)}
                    showConnectSalePlatform={false}
                  />
                )}
                {selectedPlatform === "Depop" && (
                  <DepopConfirm
                    onClose={() => setIsPlatformModalOpen(false)}
                    showConnectSalePlatform={false}
                  />
                )}
                {selectedPlatform === "Poshmark" && (
                  <PoshmarkConfirm
                    onClose={() => setIsPlatformModalOpen(false)}
                    onStepChange={() => setIsPlatformModalOpen(false)}
                    showConnectSalePlatform={false}
                  />
                )}
                {selectedPlatform === "Whatnot" && (
                  <PoshmarkConfirmDialog
                    username={
                      "username" in whatnotUserData
                        ? whatnotUserData.username
                        : undefined
                    }
                    platform={"whatnot"}
                    onConfirm={async (bankAccountId?: string | undefined) => {
                      if (
                        "cookies" in whatnotUserData &&
                        whatnotUserData.cookies
                      ) {
                        const existingWhatnotIntegration =
                          await getWhatnotIntegration();
                        if (existingWhatnotIntegration?.id) {
                          if (await doesUserHaveCardPlatform("Whatnot")) {
                            await completeUserSetupQuestionCardPlatform(
                              "Whatnot"
                            );
                          }
                          toast.info(
                            "That Whatnot account is already connected."
                          );
                          setIsPlatformModalOpen(false);
                          return;
                        }
                        await whatnotIntegrationCreate({
                          cookies: whatnotUserData.cookies,
                          username: whatnotUserData.username,
                          lastSync: new Date().toISOString(),
                          bankAccountId,
                          whatnotUserId: whatnotUserData.userId,
                        });
                        recordEvent({
                          type: "feature-adoption",
                          feature: "integration-added",
                          element: "sale-platform-whatnot",
                        });
                        if (await doesUserHaveCardPlatform("Whatnot")) {
                          const whatnotIntegration =
                            await getWhatnotIntegration();
                          await completeUserSetupQuestionCardPlatform(
                            "Whatnot"
                          );
                          if (whatnotIntegration?.id) {
                            const currentDate = new Date();
                            dispatch(
                              whatnotSyncSales(whatnotIntegration.id, {
                                start: {
                                  month: 1,
                                  year: currentDate.getFullYear() - 1,
                                },
                                end: {
                                  month: currentDate.getMonth() + 1,
                                  year: currentDate.getFullYear(),
                                } as MonthYear,
                              })
                            );
                          }
                        }
                      }
                      setIsPlatformModalOpen(false);
                    }}
                    onCancel={() => setIsPlatformModalOpen(false)}
                    showConnectSalePlatform={false}
                  />
                )}
              </Dialog>
              <Dialog
                open={isEbayUpgradeModalOpen}
                onClose={() => setIsEbayUpgradeModalOpen(false)}
                fullWidth
                maxWidth="sm"
              >
                <WantToAddAdditionalEBayStoresContent
                  onClose={() => setIsEbayUpgradeModalOpen(false)}
                />
              </Dialog>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SetupProgress;
